/**
 * A filter with nothing to refine hides itself.
 *
 * WHY. Every filter template renders its accordion — a header, a chevron, a panel — from PHP,
 * before anything knows whether the index holds any values for that attribute. The widget then
 * fills the panel in the browser. When the attribute has no values the panel stays empty, and the
 * page shows a labelled control that opens onto nothing.
 *
 * That is not a rare misconfiguration; it is the DEFAULT on most sites. A fresh install indexes
 * every public post type and ships a Categories filter, but `category` belongs to the `post` post
 * type alone: a shop indexes products, an LMS indexes courses, a brochure site indexes pages, and
 * none of them has a single category value. The filter cannot work, and until now it still drew
 * itself.
 *
 * WHEN HIDING IS SAFE. Only when there is genuinely nothing to refine. InstantSearch keeps a
 * REFINED value in its item list even when the current results no longer contain it, so an empty
 * list also means no refinement is active on that attribute — hiding can never strand a visitor
 * with a filter applied and no way to remove it. That is the same signal InstantSearch's own
 * `panel({ hidden })` uses.
 *
 * Visibility is set on every render, not once: a filter that is empty for one query and useful for
 * the next comes back.
 */

/**
 * @param {Element} element    The widget's container (the `[data-filter-type]` div).
 * @param {boolean} canRefine  Whether the widget has anything to offer.
 */
export function setFilterVisibility( element, canRefine ) {
  if ( ! element ) return;

  // The accordion is what carries the label and the chrome, so it is what has to go. A widget
  // placed by a bare shortcode has no accordion around it; then the widget itself is hidden.
  const host = element.closest( '.tgbl-filter-accordion' ) || element;

  host.classList.toggle( 'tgbl-filter--empty', ! canRefine );
  // Cleared rather than set to a value, so the stylesheet decides again when it comes back.
  host.style.display = canRefine ? '' : 'none';
}

/**
 * Hide a widget whose emptiness InstantSearch reports through its own `noRefinementRoot` class
 * (the built-in `menu` and `menuSelect` widgets), re-checked after every render of the instance.
 *
 * @param {object}  search        The InstantSearch instance.
 * @param {Element} element       The widget's container.
 * @param {string}  emptyClass    The widget's configured `noRefinementRoot` class.
 */
export function hideWhenNoRefinement( search, element, emptyClass ) {
  if ( ! search || ! element ) return;

  const apply = () => setFilterVisibility( element, ! element.querySelector( `.${ emptyClass }` ) );

  search.on( 'render', apply );
}
